import { createClient, type ContentfulClientApi } from 'contentful'
import type {
  Article,
  Category,
  Portal,
  Footer,
  ContentfulCollection,
  ContentfulQuery,
} from '@/types/contentful'

// Module-level singleton for server-side Contentful client
// Shared across all composable calls during the same server process
let _serverClient: ContentfulClientApi<undefined> | null = null

function getServerClient(): ContentfulClientApi<undefined> {
  if (_serverClient) return _serverClient

  const config = useRuntimeConfig()
  const space = config.CONTENTFUL_SPACE_ID as string
  const accessToken = config.CONTENTFUL_ACCESS_TOKEN as string
  const environment = (config.CONTENTFUL_ENVIRONMENT as string) || 'master'

  if (!space || !accessToken) {
    throw new Error('CONTENTFUL_SPACE_ID and CONTENTFUL_ACCESS_TOKEN must be set in runtimeConfig')
  }

  _serverClient = createClient({ space, accessToken, environment })
  return _serverClient
}

/**
 * Composable for Contentful data access.
 *
 * - Server-side (prerender/SSR): calls Contentful API directly via singleton client
 * - Client-side (browser): calls /api/contentful proxy (cached, token not exposed)
 *
 * All methods return the same shape regardless of environment.
 */
export const useContentful = () => {
  // Core query function — routes to direct SDK or proxy based on environment
  const getEntries = async <T>(
    query: Record<string, unknown>,
  ): Promise<ContentfulCollection<T>> => {
    if (import.meta.server) {
      const client = getServerClient()
      return client.getEntries(query) as unknown as Promise<ContentfulCollection<T>>
    } else {
      return $fetch<ContentfulCollection<T>>('/api/contentful', { query })
    }
  }

  // Get articles
  const getArticles = async (query: Partial<ContentfulQuery> = {}) => {
    return getEntries<Article>({
      content_type: 'article',
      ...query,
    })
  }

  // Get articles by parent (portal)
  const getArticlesByParent = async (parentId: string, query: Partial<ContentfulQuery> = {}) => {
    try {
      return await getArticles({
        'fields.parent.sys.id': parentId,
        ...query,
      })
    } catch (error) {
      console.error(`Error fetching articles for parent "${parentId}":`, error)
      return { items: [], includes: {}, total: 0, skip: 0, limit: 0, sys: { type: 'Array' } } as any
    }
  }

  // Get articles by category
  const getArticlesByCategory = async (
    categoryId: string,
    query: Partial<ContentfulQuery> = {},
  ) => {
    try {
      return await getArticles({
        'fields.category.sys.id': categoryId,
        ...query,
      })
    } catch (error) {
      console.error(`Error fetching articles for category "${categoryId}":`, error)
      return { items: [], includes: {}, total: 0, skip: 0, limit: 0, sys: { type: 'Array' } } as any
    }
  }

  // Get a single article by slug (tries multiple slug variations)
  const getArticleBySlug = async (slug: string) => {
    const variations = [slug, `/${slug}`, slug.trim(), `/${slug.trim()}`]

    for (const variation of variations) {
      try {
        const response = await getArticles({
          'fields.slug': variation,
          limit: 1,
          include: 2,
        })

        if (response.items.length > 0) {
          const article = response.items[0] as any
          article.includes = response.includes
          return article
        }
      } catch (error) {
        continue
      }
    }

    return null
  }

  // Get categories
  const getCategories = async (query: Partial<ContentfulQuery> = {}) => {
    return getEntries<Category>({
      content_type: 'category',
      ...query,
    })
  }

  // Get a single category by slug
  const getCategoryBySlug = async (slug: string) => {
    const response = await getCategories({
      'fields.slug': slug,
      limit: 1,
      include: 2,
    })
    return response.items[0] || null
  }

  // Get portals
  const getPortals = async (query: Partial<ContentfulQuery> = {}) => {
    return getEntries<Portal>({
      content_type: 'portal',
      ...query,
    })
  }

  // Get footer settings (single)
  const getFooter = async () => {
    const response = await getEntries<Footer>({
      content_type: 'footer',
      limit: 1,
      include: 2,
    })
    const footer = response.items[0]
    if (!footer) return null
    return {
      ...footer,
      includes: response.includes,
    }
  }

  // Get a single portal by slug
  const getPortalBySlug = async (slug: string) => {
    try {
      const response = await getPortals({
        'fields.slug': slug,
        limit: 1,
        include: 2,
      })
      const portal = response.items[0]
      if (portal) {
        return {
          ...portal,
          includes: response.includes,
        }
      }
      return null
    } catch (error) {
      console.error(`Error fetching portal with slug "${slug}":`, error)
      return null
    }
  }

  // Get animals (articles with parent 'portal-djurfakta')
  const getAnimals = async (query: Partial<ContentfulQuery> = {}) => {
    return getArticlesByParent('portal-djurfakta', {
      order: 'fields.title',
      ...query,
    })
  }

  return {
    getEntries,
    getArticles,
    getArticlesByParent,
    getArticlesByCategory,
    getArticleBySlug,
    getCategories,
    getCategoryBySlug,
    getPortals,
    getPortalBySlug,
    getFooter,
    getAnimals,
  }
}
